import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import ChallengeImage from "../../components/ChallengeImage";
import StageTabs, { useAdminTheme } from "../../components/StageTabs";
import { assetUrl, ensureThemeJsonArray } from "../../../functions/themeAssets";
import { imageSize, savePuzzleGrid, uploadPuzzleImage } from "../../functions/stageApi";

const VALID_EXT = ["jpg", "jpeg", "png"];
const MAX_MB = 5;

/** Stage 4 — heading, jigsaw picture and grid (jigsaw_theme.php). */
export default function Jigsaw() {
  const { adminToken, currentTheme, data, save, reload } = useAdminTheme();
  const [title, setTitle] = useState("");
  const [rows, setRows] = useState("");
  const [cols, setCols] = useState("");
  const [imgCount, setImgCount] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!data) return;
    setTitle(String(data.landing_page_title4 ?? ""));
    setRows(String(data.row ?? ""));
    setCols(String(data.col ?? ""));
    setImgCount(String(data.img_count ?? ""));
  }, [data]);

  const onSaveTitle = async () => {
    if (title.trim().length === 0 || title.length > 150) {
      return Swal.fire(title.trim().length === 0 ? "The title cannot be empty or contain only spaces." : "You exceed the maximum limit of the title.", "", "error");
    }
    try {
      await save({ landing_page_title4: title });
      Swal.fire({ title: "Data Updated", icon: "success", timer: 1000, showConfirmButton: false });
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    }
  };

  const onUpload = async (e) => {
    e.preventDefault();
    if (!file) return Swal.fire("Please choose an image", "", "error");
    const ext = file.name.split(".").pop().toLowerCase();
    if (!VALID_EXT.includes(ext)) return Swal.fire("Unsupported file format", "", "error");
    if (file.size > MAX_MB * 1024 * 1024) return Swal.fire(`Filesize exceeed max limit ${MAX_MB} Mb`, "", "error");
    try {
      const { width, height } = await imageSize(file);
      if (width !== 1920 || height !== 1080) return Swal.fire("Image dimension must be 1920x1080", "", "error");
    } catch (err) {
      return Swal.fire(err.message, "", "error");
    }

    const fd = new FormData();
    fd.append("themeName", currentTheme);
    fd.append("file", file);
    setBusy(true);
    try {
      await uploadPuzzleImage({ token: adminToken, formData: fd });
      await reload();
      setFile(null);
      Swal.fire({ title: "Successfully Updated", icon: "success", timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire(err.message, "", "error");
    } finally {
      setBusy(false);
    }
  };

  const onSaveGrid = async () => {
    const r = parseInt(rows, 10);
    const c = parseInt(cols, 10);
    const n = parseInt(imgCount, 10);
    if (!(r >= 2 && r <= 12)) return Swal.fire("Row must be between 2 and 12", "", "error");
    if (!(c >= 2 && c <= 12)) return Swal.fire("Column must be between 2 and 12", "", "error");
    if (!(n >= 2 && n <= 8)) return Swal.fire("Image count must be between 2 and 8", "", "error");

    setBusy(true);
    try {
      const res = await savePuzzleGrid({ token: adminToken, themeName: currentTheme, rows: r, cols: c, imgCount: n });
      await reload();
      Swal.fire({
        title: "Data Updated",
        text: res.sliced ? "" : "Upload a jigsaw image, then save again to cut it into pieces.",
        icon: "success",
        timer: res.sliced ? 1200 : undefined,
      });
    } catch (err) {
      Swal.fire(err.message, "", "error");
    } finally {
      setBusy(false);
    }
  };

  const slices = ensureThemeJsonArray(data?.img_array).filter(Boolean);

  return (
    <>
      <StageTabs />

      <div className="bp-card">
        <h4>Add Custom Title:</h4>
        <input type="text" className="bp-input" maxLength={150} value={title} onChange={(e) => setTitle(e.target.value)} />
        <p className="bp-note">
          * This title will reflect on the Challenege 4 of game page &nbsp; * Character limit of the text is 150 characters.
        </p>
        <div className="bp-actions">
          <button type="button" className="bp-btn" onClick={onSaveTitle}>
            Save
          </button>
        </div>
      </div>

      <ChallengeImage stage={4} />

      <div className="bp-card">
        <h4>Add Jigsaw Image</h4>
        <div className="bp-row">
          <div className="bp-col">
            <form onSubmit={onUpload}>
              <input
                type="file"
                className="bp-input"
                accept=".jpg,.jpeg,.png"
                onChange={(e) => setFile(e.target.files[0] || null)}
              />
              <p className="bp-note">* Allowed Photo Formats - JPG, JPEG, PNG &nbsp; * Allowed Dimensions - 1920x1080</p>
              <div className="bp-actions">
                <button type="submit" className="bp-btn" disabled={busy}>
                  <i className="fa-solid fa-cloud-arrow-up" /> Upload
                </button>
              </div>
            </form>

            <h4 style={{ marginTop: 24 }}>Select Grid Size</h4>
            <div className="bp-grid-input">
              <span>Row</span>
              <input type="number" min="2" max="12" value={rows} onChange={(e) => setRows(e.target.value)} />
            </div>
            <div className="bp-grid-input">
              <span>Column</span>
              <input type="number" min="2" max="12" value={cols} onChange={(e) => setCols(e.target.value)} />
            </div>
            <div className="bp-grid-input">
              <span>Image count</span>
              <input type="number" min="2" max="8" value={imgCount} onChange={(e) => setImgCount(e.target.value)} />
            </div>
            <div className="bp-actions">
              <button type="button" className="bp-btn" onClick={onSaveGrid} disabled={busy}>
                Save
              </button>
            </div>
          </div>

          <div className="bp-col" style={{ textAlign: "center" }}>
            {data?.puzzle_image && <img className="bp-preview large" src={assetUrl(data.puzzle_image)} alt="Uploaded jigsaw" />}
            {slices.length > 0 && (
              <>
                <p className="bp-note" style={{ color: "#555" }}>
                  Pieces players get ({slices.length})
                </p>
                <div className="bp-slices">
                  {slices.map((s) => (
                    <img key={s} src={assetUrl(s)} alt="" />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

